import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { handleError, validateNonEmpty, validateEmail, validatePhone, isValidLocalPhoneInput, fetchForInput, removeNullsAndEmptys, getFileNameWithoutExtension, isEditing } from "../../utils/lib";
import { TextField, Autocomplete, Menu, MenuItem, ListItemIcon, ListItemText, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Avatar, CircularProgress, InputAdornment } from "@mui/material";
import { PhotoCamera, PhotoLibrary, Delete } from "@mui/icons-material";
import { uploadPhotoAndThumbnail } from "../../utils/fileUploadHelper";
import { useSnackbar } from "../../providers/SnackBarProvider";
import { useAppBar } from "../../providers/AppBarProvider";
import { useConfig } from '../../providers/ConfigProvider';
import { useTranslation } from 'react-i18next';
import EditPage from "./EditPage";
import useOwnerStore from "../../store/ownerStore";
import { v4 as uuidv4 } from 'uuid';
import InlinePhotoCapture from "../../components/InlinePhotoCapture";

const EditOwner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const {updateTitle, toggleFab} = useAppBar();
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const {config, isLoadingConfig} = useConfig();
  const { t } = useTranslation();

  const { 
    owner, 
    setOwner, 
    updateOwner, 
  } = useOwnerStore();

  const { updateOwnerFromLocation } = location.state || {};

  const [regions, setRegions] = useState([]);
  const [fiscalTypes, setFiscalTypes] = useState([]);
  const [mobilePrefixes, setMobilePrefixes] = useState([]);
  const [selectedPrefix, setSelectedPrefix] = useState(null);

  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [uploadedImages, setUploadedImages] = useState({ original: null, avatar: null });
  const [photoDeleted, setPhotoDeleted] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showInlineCamera, setShowInlineCamera] = useState(false);
  const fileInputRef = useRef(null);

  const [errors, setErrors] = useState({});
  const [refs] = useState({
    name: useRef(null),
    address: useRef(null),
    region: useRef(null),
    phone: useRef(null),
    email: useRef(null),
    regional_id: useRef(null),
    fiscal_type: useRef(null),
    notes: useRef(null),
  });

  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [existingOwner, setExistingOwner] = useState(null);
  const [showExistingOwnerModal, setShowExistingOwnerModal] = useState(false);

  useEffect(() => {

    if (updateOwnerFromLocation) {
      setOwner(updateOwnerFromLocation);
    }

    const currentThumbUrl = !photoDeleted ? (capturedPhoto?.url || owner?.thumb_url || '') : '';
    
    updateTitle(
      currentThumbUrl,
      isEditing(owner) ? t('edit.updateOwner') : t('edit.newOwner'),
      t('common.completeData'),
      false,
      !currentThumbUrl ? PhotoCamera : null,
      handleAvatarClick
    );

    toggleFab(false);

    const fetchData = async () => {
      try {
        const ownersForInput = await fetchForInput('owners', 'owners_for_input');
        setRegions(ownersForInput.regions);
        setFiscalTypes(ownersForInput.finance_types_fiscal);
        
        // Cargar prefijos móviles
        if (ownersForInput.regions_mobile_prefix) {
          setMobilePrefixes(ownersForInput.regions_mobile_prefix);
          
          // Establecer el prefijo por defecto si existe en la configuración
          const defaultPrefix = config?.vet?.default_mobile_country_prefix;
          if (defaultPrefix && !owner.country_code) {
            const defaultPrefixObj = ownersForInput.regions_mobile_prefix.find(
              prefix => prefix.mobile_prefix === defaultPrefix
            );
            if (defaultPrefixObj) {
              setSelectedPrefix(defaultPrefixObj);
            }
          } else if (owner.country_code) {
            // Si el propietario ya tiene un country_code, usar ese
            const ownerPrefix = ownersForInput.regions_mobile_prefix.find(
              prefix => prefix.country_code === owner.country_code
            );
            if (ownerPrefix) {
              setSelectedPrefix(ownerPrefix);
            }
          }
        }
        
      } catch (error) {
        handleError(error, snackbar);
      } finally {
        setIsLoading(false);

        setTimeout(() => {
          if (refs.name.current) {
            refs.name.current.focus();
          }
        }, 100);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const currentThumbUrl = !photoDeleted ? (capturedPhoto?.url || owner?.thumb_url || '') : '';

    updateTitle(
      currentThumbUrl,
      isEditing(owner) ? t('edit.updateOwner') : t('edit.newOwner'),
      t('common.completeData'),
      false,
      !currentThumbUrl ? PhotoCamera : null,
      handleAvatarClick
    );
  }, [capturedPhoto, owner?.id, config, photoDeleted]);

  useEffect(() => {
    // Solo verificar si no estamos editando un propietario existente
    if (isEditing(owner) || !owner.phone || !selectedPrefix?.country_code) {
      return;
    }

    if (!isValidLocalPhoneInput(owner.phone)) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsCheckingPhone(true);
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}owners`,
          { 
            params: { 
              exists_phone: owner.phone, 
              country_code: selectedPrefix.country_code 
            },
            withCredentials: true 
          }
        );

        //console.log(response.data);

        if (response.data.status === 'SUCCESS' && response.data.data) {
          setExistingOwner(response.data.data);
          setShowExistingOwnerModal(true);
        } else {
          setExistingOwner(null);
        }
      } catch (error) {
        // Error silencioso para no molestar al usuario mientras escribe
        console.error('Error checking phone:', error);
      } finally {
        setIsCheckingPhone(false);
      }
    }, 800); // Debounce de 800ms

    return () => clearTimeout(timeoutId);
  }, [owner.phone, selectedPrefix?.country_code, owner.id]);

  useEffect(() => {
    // Solo verificar si no estamos editando un propietario existente
    if (isEditing(owner) || !owner.email || owner.email.trim() === '') {
      return;
    }

    // Validación básica de formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(owner.email)) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsCheckingEmail(true);
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}owners`,
          { 
            params: { 
              exists_email: owner.email
            },
            withCredentials: true 
          }
        );

        if (response.data.status === 'SUCCESS' && response.data.data) {
          setExistingOwner(response.data.data);
          setShowExistingOwnerModal(true);
        } else {
          setExistingOwner(null);
        }
      } catch (error) {
        // Error silencioso para no molestar al usuario mientras escribe
        console.error('Error checking email:', error);
      } finally {
        setIsCheckingEmail(false);
      }
    }, 800); // Debounce de 800ms

    return () => clearTimeout(timeoutId);
  }, [owner.email, owner.id]);

  const handlePhotoAndThumbUpload = async (originalFile) => {
    try {
      setIsUploadingImage(true);

      const result = await uploadPhotoAndThumbnail({
        originalFile,
        tableName: 'owners',
        onError: (error) => {
          snackbar(t('common.errorUploadingImages') + error);
        }
      });

      setUploadedImages({
        original: result.original,
        avatar: result.thumbnail
      });
      
      setPhotoDeleted(false);
      
    } catch (error) {
      snackbar(t('common.errorUploadingImages') + (error.message || ''));
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCameraCapture = () => {
    handleMenuClose();
    setShowInlineCamera(true);
  };

  const handleGallerySelect = () => {
    handleMenuClose();
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemovePhoto = () => {
    handleMenuClose();
    setPhotoDeleted(true);
    setCapturedPhoto(null);
    setUploadedImages({ original: null, avatar: null });
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      const unique_id = uuidv4();
      const extension = file.name.split('.').pop();
      const newFileName = `${unique_id}.${extension}`;
      
      const renamedFile = new File([file], newFileName, { type: file.type });
      
      const processedPhoto = {
        file: renamedFile,
        url: URL.createObjectURL(file),
        name: newFileName,
        size: file.size,
        type: file.type,
        isImage: true,
        isVideo: false,
        sizeInMB: (file.size / 1024 / 1024).toFixed(2)
      };
      
      setCapturedPhoto(processedPhoto);
      setPhotoDeleted(false);
      await handlePhotoAndThumbUpload(renamedFile);
    }
    
    event.target.value = '';
  };

  const handleInlinePhotoConfirm = async (file, previewUrl) => {
    setCapturedPhoto({
      url: previewUrl,
      file: file,
      isImage: true
    });

    setPhotoDeleted(false);
    await handlePhotoAndThumbUpload(file);
  };

  const handleAssociateExistingOwner = async () => {
    if (!existingOwner) return;

    // Si el propietario ya pertenece a la misma veterinaria, simplemente navegar a su ficha
    if (existingOwner.type === 'SAME_VET') {
      setShowExistingOwnerModal(false);
      navigate('/main/view-owner', { state: { id: existingOwner.id, updateLastView: false } }, { replace: true });
      return;
    }

    // Si es de otra veterinaria, asociarlo primero
    try {
      setIsLoading(true);
      await axios.put(
        `${import.meta.env.VITE_API_URL}owners`,
        {},
        { 
          params: { 
            associate_to_vet: true, 
            id_owner: existingOwner.id 
          },
          withCredentials: true 
        }
      );

      snackbar(t('edit.ownerAssociated', { name: config?.vet?.owner_naming_es || 'Cliente' }));
      setShowExistingOwnerModal(false);
      navigate('/main/view-owner', { state: { id: existingOwner.id, updateLastView: false } }, { replace: true });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueWithNewOwner = () => {
    setShowExistingOwnerModal(false);
    setExistingOwner(null);
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    updateOwner({ [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (
      !validateNonEmpty("name", owner.name, setErrors, refs.name) ||
      !validatePhone("phone", owner.phone, setErrors, refs.phone, true) ||
      !validateEmail("email", owner.email, setErrors, refs.email, true)
    )
      return;

    save();
  };

  const save = async () => {
    setIsLoading(true);

    const ownerToSend = { ...owner };

    ownerToSend.resources = [];
    if (uploadedImages.original && uploadedImages.avatar) {
      ownerToSend.resources = [
        {
          file_name: uploadedImages.original.file_name,
          table_name: "owners",
          type: "PUBLIC",
          unique_id: uploadedImages.original.unique_id
        },
        {
          file_name: uploadedImages.avatar.file_name,
          table_name: "owners", 
          type: "PUBLIC",
          unique_id: uploadedImages.avatar.unique_id
        }
      ];
    }

    if (photoDeleted) {
      ownerToSend.photo_deleted = 1;
    }

    // Agregar country_code si hay un prefijo seleccionado
    if (selectedPrefix && selectedPrefix.country_code) {
      ownerToSend.country_code = selectedPrefix.country_code;
    } 

    try {      
      let response;

      if (isEditing(owner)) {
        response = await axios.put(
          `${import.meta.env.VITE_API_URL}owners`,
          removeNullsAndEmptys(ownerToSend),
          { params: { update: true, id: owner.id }, withCredentials: true }
        );
      } else {
        response = await axios.post(
          `${import.meta.env.VITE_API_URL}owners`,
          removeNullsAndEmptys(ownerToSend),
          { withCredentials: true }
        );
      }

      navigate('/main/view-owner', { state: { id: response.data.data.id, updateLastView: false } }, { replace: true });
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e, fieldName) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      
      const fieldOrder = ['name', 'address', 'region', 'phone', 'email', 'regional_id', 'fiscal_type', 'notes'];
      const currentIndex = fieldOrder.indexOf(fieldName);
      
      if (currentIndex === fieldOrder.length - 1) {
        handleSubmit(e);
      } else if (currentIndex >= 0 && currentIndex < fieldOrder.length - 1) {
        const nextField = fieldOrder[currentIndex + 1];
        if (refs[nextField] && refs[nextField].current) {
          refs[nextField].current.focus();
        }
      }
    }
  };

  return (
    <EditPage 
      onSubmit={handleSubmit} 
      isLoading={isLoading || isLoadingConfig || isUploadingImage || isCheckingPhone || isCheckingEmail}
    >
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleCameraCapture}>
          <ListItemIcon>
            <PhotoCamera />
          </ListItemIcon>
          <ListItemText>{t('common.camera')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleGallerySelect}>
          <ListItemIcon>
            <PhotoLibrary />
          </ListItemIcon>
          <ListItemText>{t('common.gallery')}</ListItemText>
        </MenuItem>
        {(capturedPhoto || owner.photo_url || owner.thumb_url) && !photoDeleted && (
          <MenuItem onClick={handleRemovePhoto}>
            <ListItemIcon>
              <Delete />
            </ListItemIcon>
            <ListItemText>{t('common.remove')}</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileSelect}
      />

      <InlinePhotoCapture 
        open={showInlineCamera}
        onPhotoConfirm={handleInlinePhotoConfirm}
        onClose={() => setShowInlineCamera(false)}
      />

      <Dialog
        open={showExistingOwnerModal}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('edit.existingOwnerTitle', { name: config?.vet?.owner_naming_es || 'Cliente' })}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
            {existingOwner?.thumb_url && (
              <Avatar
                src={existingOwner.thumb_url}
                sx={{ width: 100, height: 100 }}
              />
            )}
            <Box sx={{ textAlign: 'center' }}>
              <strong>{existingOwner?.name}</strong>
              {existingOwner?.phone && <div>{t('edit.ownerPhone')} {existingOwner?.phone}</div>}
              {existingOwner?.email && <div>{t('edit.ownerEmail')} {existingOwner?.email}</div>}
            </Box>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              {existingOwner?.type === 'SAME_VET' ? (
                <>
                  {t('edit.existingOwnerLocal', { name: config?.vet?.owner_naming_es?.toLowerCase() || 'cliente' })}
                </>
              ) : (
                <>
                  {t('edit.existingOwnerPlatform', { name: config?.vet?.owner_naming_es?.toLowerCase() || 'cliente' })}
                </>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button onClick={handleContinueWithNewOwner} color="inherit">
            {t('edit.createNew')}
          </Button>
          <Button onClick={handleAssociateExistingOwner} variant="contained" color="primary">
            {existingOwner?.type === 'SAME_VET' ? t('edit.viewProfile') : t('edit.associateExisting')}
          </Button>
        </DialogActions>
      </Dialog>

      <TextField
        fullWidth
        margin="normal"
        label={t('common.nameStar')}
        name="name"
        value={owner.name}
        onChange={handleChange}
        inputRef={refs.name}
        error={!!errors.name}
        helperText={errors.name}
        onKeyDown={(e) => handleKeyDown(e, 'name')}
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('common.address')}
        name="address"
        value={owner.address}
        onChange={handleChange}
        inputRef={refs.address}
        onKeyDown={(e) => handleKeyDown(e, 'address')}
      />
      <Autocomplete
        fullWidth
        options={regions}
        getOptionLabel={(option) => (option ? option.friendly_name : "")}
        value={owner.region || null}
        onChange={(e, newValue) =>
          updateOwner({
            region: newValue,
          })
        }
        renderInput={(params) => (
          <TextField 
            {...params} 
            label={t('common.region')} 
            name="region" 
            margin="normal" 
            inputRef={refs.region}
            onKeyDown={(e) => handleKeyDown(e, 'region')}
          />
        )}
      />
      <Box sx={{ display: 'flex', gap: 2, marginTop: '16px', marginBottom: '8px' }}>
        <Autocomplete
          sx={{ width: '200px' }}
          options={mobilePrefixes}
          getOptionLabel={(option) => {
            if (!option) return "";
            return option.mobile_prefix 
              ? `+${option.mobile_prefix} (${option.country})` 
              : option.country;
          }}
          value={selectedPrefix || null}
          onChange={(e, newValue) => setSelectedPrefix(newValue)}
          disableClearable={true}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label={t('common.prefix')} 
              margin="none"
            />
          )}
        />
        <TextField
          fullWidth
          label={t('common.phone')}
          name="phone"
          value={owner.phone}
          onChange={handleChange}
          inputRef={refs.phone}
          error={!!errors.phone}
          helperText={errors.phone}
          onKeyDown={(e) => handleKeyDown(e, 'phone')}
          InputProps={{
            endAdornment: isCheckingPhone ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>
      <TextField
        fullWidth
        margin="normal"
        label={t('common.email')}
        name="email"
        value={owner.email}
        onChange={handleChange}
        error={!!errors.email}
        helperText={errors.email}
        inputRef={refs.email}
        onKeyDown={(e) => handleKeyDown(e, 'email')}
        InputProps={{
          endAdornment: isCheckingEmail ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null,
        }}
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('common.regionalId')}
        name="regional_id"
        value={owner.regional_id}
        onChange={handleChange}
        inputRef={refs.regional_id}
        onKeyDown={(e) => handleKeyDown(e, 'regional_id')}
      />
      <Autocomplete
        fullWidth
        options={fiscalTypes}
        getOptionLabel={(option) => (option ? option.name : "")}
        value={owner.fiscal_type || null}
        onChange={(e, newValue) => {
          updateOwner({ fiscal_type: newValue });
        }}
        renderInput={(params) => (
          <TextField 
            {...params} 
            label={t('common.fiscalType')} 
            name="fiscal_type" 
            margin="normal" 
            inputRef={refs.fiscal_type}
            onKeyDown={(e) => handleKeyDown(e, 'fiscal_type')}
          />
        )}
      />
      <TextField
        fullWidth
        margin="normal"
        label={t('common.notes')}
        name="notes"
        value={owner.notes}
        onChange={handleChange}
        multiline
        rows={4}
        inputRef={refs.notes}
        onKeyDown={(e) => handleKeyDown(e, 'notes')}
      />
    </EditPage>
  );
};

export default EditOwner;
